import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  exportRecapAsMarkdown,
  exportRecapAsAnkiTSV,
  exportRecapAsPDF,
  copyRecapToClipboard,
} from '../utils/flashcardExport';
import '../styles/RecapModal.css';

function FlashCard({ card, index }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="flashcard">
      <div className="flashcard-number">#{index + 1}</div>
      <div className="flashcard-question">{card.q}</div>
      {revealed ? (
        <div className="flashcard-answer">{card.a}</div>
      ) : (
        <button
          type="button"
          className="flashcard-reveal-btn"
          onClick={() => setRevealed(true)}
        >
          Show answer
        </button>
      )}
    </div>
  );
}

function RecapExportMenu({ summary, flashcards, paper }) {
  const [open, setOpen] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handle = async (action) => {
    setOpen(false);
    try {
      if (action === 'md') exportRecapAsMarkdown(summary, flashcards, paper);
      else if (action === 'anki') exportRecapAsAnkiTSV(flashcards, paper);
      else if (action === 'pdf') exportRecapAsPDF(summary, flashcards, paper);
      else if (action === 'clipboard') {
        await copyRecapToClipboard(summary, flashcards, paper);
        setJustCopied(true);
        setTimeout(() => setJustCopied(false), 1600);
      }
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed: ' + (e.message || e));
    }
  };

  return (
    <div className="recap-export-menu" ref={rootRef}>
      <button
        type="button"
        className="recap-export-trigger"
        onClick={() => setOpen(o => !o)}
        title="Export recap"
      >
        {justCopied ? '✓ Copied' : 'Export ▾'}
      </button>
      {open && (
        <div className="recap-export-dropdown" role="menu">
          <button type="button" role="menuitem" onClick={() => handle('md')}>
            <span>📝</span><span>Markdown (.md)</span>
          </button>
          <button type="button" role="menuitem" onClick={() => handle('anki')}>
            <span>🗂</span><span>Anki (.txt)</span>
          </button>
          <button type="button" role="menuitem" onClick={() => handle('pdf')}>
            <span>📄</span><span>PDF (.pdf)</span>
          </button>
          <button type="button" role="menuitem" onClick={() => handle('clipboard')}>
            <span>📋</span><span>Copy to clipboard</span>
          </button>
        </div>
      )}
    </div>
  );
}

function RecapModal({ recap, paper, loading, error, onClose }) {
  // Close on Escape or backdrop click
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="recap-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="recap-modal" role="dialog" aria-modal="true" aria-label="Session recap">
        <div className="recap-modal-header">
          <span className="recap-modal-title">Session Recap</span>
          <div className="recap-modal-actions">
            {!loading && recap && (
              <RecapExportMenu
                summary={recap.summary}
                flashcards={recap.flashcards}
                paper={paper}
              />
            )}
            <button type="button" className="recap-close-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="recap-modal-body">
          {loading ? (
            <div className="recap-loading">
              <div className="recap-spinner" />
              <p>Generating your session recap…</p>
            </div>
          ) : recap ? (
            <>
              <section className="recap-section">
                <h3 className="recap-section-title">What you covered</h3>
                <div className="recap-summary">
                  <ReactMarkdown>{recap.summary}</ReactMarkdown>
                </div>
              </section>

              {recap.flashcards && recap.flashcards.length > 0 && (
                <section className="recap-section">
                  <h3 className="recap-section-title">
                    Flashcards
                    <span className="recap-card-count">{recap.flashcards.length} cards</span>
                  </h3>
                  <div className="flashcard-list">
                    {recap.flashcards.map((card, i) => (
                      <FlashCard key={i} card={card} index={i} />
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <p className="recap-error">{error || 'Something went wrong generating the recap.'}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecapModal;
